<!--
    This component is responsible to create a list view representaion of the given items.

    TableHeaders:
        It will create a table header for each FilterOption. The option can configure how to behave or 
        not to be displayed at all with the optional TableFilterOption.


        See TableHeader for more info.

-->
<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { pressESC } from '../../../helper/Extensions';
	import IconButton from '../../buttons/IconButton.svelte';
	import DefaultSwitch from '../../switch/DefaultSwitch.svelte';
	import {
		generateTableGrid,
		type FilterOption,
		type ListItemInfo,
		type MultiSelectionItem,
		type SimpleListStore,
		type StoreListener
	} from '../../../types/ListTypes';
	import DefaultEmptyListPlaceholder from './DefaultEmptyListPlaceholder.svelte';
	import DefaultSimpleListFilterContainer from './DefaultSimpleListFilterContainer.svelte';
	import TableHeader from './TableHeader.svelte';

	interface Props {
		id?: string | null;
		store: SimpleListStore;
		itemComponent: any;
		selectedItemCount: number;
		onSelection: (info: ListItemInfo) => void;
		onDblClick: (item: MultiSelectionItem) => void;
		onChangeSortOrder: (header: FilterOption) => void;
		onHeaderResized: () => void;
		onRequestContextmenu: (info: ListItemInfo) => void;
		onDragStartRequest: (info: ListItemInfo) => void;
		onTableHeaderVisibilityChange: () => void;
		onCustomEvent: (e: any) => void;
		onAddRequest: (index: number) => void;
		onChildDeleted?: (item: any) => void;
		onEditRequest?: (item: MultiSelectionItem) => void;
	}

	let {
		id = null,
		store = $bindable(),
		itemComponent,
		selectedItemCount,
		onSelection,
		onDblClick,
		onChangeSortOrder,
		onHeaderResized,
		onRequestContextmenu,
		onDragStartRequest,
		onTableHeaderVisibilityChange,
		onCustomEvent,
		onAddRequest,
		onChildDeleted = undefined,
		onEditRequest = undefined
	}: Props = $props();

	//Generates the table grid
	let listStyle = $derived(generateTableGrid(store.listFilter));

	//Show the table setting window
	let showTableSetting: boolean = $state(false);

	//Should the filter slot be shown? This is internal use and will be set to true if you click on the filter icon button.
	let showFilter: boolean = false;

	let scrollContainer: HTMLElement | undefined = $state();

	let scrollListener: HTMLElement | undefined = $state();
	let observer: IntersectionObserver;
	let loading: boolean = $state(false);
	let loopCount: number = $state(0);

	let storeListener: StoreListener = {
		onFilterApplied: () => {
			//If the scroll listener is not yet bound do not remove the observer
			if (scrollListener) {
				//We have to remove the listener otherwise the event may not be send again
				if (observer) {
					observer.disconnect();
					//Add it back again
					observer.observe(scrollListener);
				}
			}
		}
	};

	async function requestLoadMore(element: HTMLElement) {
		if (loading) return;
		loading = true;

		const total = store.data.length + store.listFilter.deletedCounter;
		const size = store.listFilter.size || 0;
		const pageSizeOk = total % size === 0;
		const nextPagePossible = store.listFilter.page * size <= total;

		//Check if we should actually load more.
		if (!pageSizeOk || !nextPagePossible) {
			loading = false;
			return;
		}

		//Make the effective load call
		await store.loadData();

		loading = false;

		// Check if marker is still visible (screen too big)
		if (scrollContainer && element.getBoundingClientRect().top < scrollContainer.getBoundingClientRect().bottom) {

			//Simple loop protection
			if (loopCount < 7) {
				loopCount += 1
				//If it is try to load more
				await requestLoadMore(element);
				//Reset the loop count after
				loopCount = 0
			}
		}
	}

	function setupObserver(element: HTMLElement) {
		observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					requestLoadMore(element);
				}
			},
			{
				root: scrollContainer,
				rootMargin: '20px', // when 20px left
				threshold: 0
			}
		);

		observer.observe(element);
	}

	onMount(() => {
		store.addNotifier(storeListener);
	});

	onDestroy(() => {
		//Remove the listener again
		store.removeNotifier(storeListener);

		if (observer) {
			observer.disconnect();
		}
	});
</script>

<DefaultSimpleListFilterContainer
	bind:simpleListConfiguration={store.listConfig}
	bind:listFilter={store.listFilter}
	{showFilter}
/>

<div class="flex relative grow" class:overflow-y-auto={store.listConfig.growHeight}>
	<div
		id={store.listFilter.localStorageKey ? store.listFilter.localStorageKey : id}
		name="list-container"
		class="grow"
		class:overflow-y-auto={store.listConfig.growHeight}
		bind:this={scrollContainer}
		use:pressESC={() => {
			showTableSetting = false;
		}}
	>
		<!--Table Headers-->
		<div
			style={`top: ${store.listConfig.tableModeConfig.tableHeaderOffset}px;` + listStyle}
			class="w-full bg-white dark:bg-dark-100 sticky z-20"
		>
			{#each store.listFilter.filterOptions as header, index}
				{#if header.tableOption?.showAsTableHeader != false}
					<TableHeader
						{header}
						simpleListListFilter={store.listFilter}
						onSort={(header) => {
							onChangeSortOrder(header);
						}}
						onHeaderResized={() => {
							//Re generate the grid
							listStyle = generateTableGrid(store.listFilter);
							//Inform the parent that the table header was resized
							onHeaderResized();
						}}
					/>
				{/if}
			{/each}
		</div>

		<!--Show place holder if data is empty-->
		{#if store.data.length <= 0}
			<DefaultEmptyListPlaceholder
				showAdd={store.listConfig.showEmptyAdd}
				onAddRequest={(index) => onAddRequest(index)}
			/>
		{:else}
			{#each store.data as item, index}
				{@const SvelteComponent = itemComponent}
				<SvelteComponent
					{store}
					style={listStyle}
					onClick={(e: MouseEvent) => {
						onSelection({
							item: item,
							index: index,
							event: e
						});
					}}
					{onCustomEvent}
					onDblClick={(e: MouseEvent) => {
						onDblClick(item);
					}}
					onChildDeleted={() => {
						store.listFilter.deletedCounter += 1;
						onChildDeleted?.(item);
					}}
					onEditRequest={() => {
						onEditRequest?.(item);
					}}
					onRequestContextmenu={(e: MouseEvent) => {
						onRequestContextmenu({
							item: item,
							index: index,
							event: e
						});
					}}
					onDragStartRequest={(event: DragEvent) => {
						onDragStartRequest({
							item: item,
							index: index,
							event: event
						});
					}}
					{item}
					{index}
					filterOptions={store.listFilter.filterOptions}
					viewMode={store.listConfig.viewMode}
					selected={item.selected}
					{selectedItemCount}
				/>
			{/each}

			{#if store.listFilter.size || 0 > 0}
				<div bind:this={scrollListener} class="h-2" use:setupObserver></div>
			{/if}
		{/if}
	</div>

	<div class="pl-2">
		<!-- Table header settings-->
		{#if store.listConfig.tableModeConfig?.showTableConfig}
			<div
				class="pt-1 sticky right-0 z-20"
				style="height: 48px; top: {store.listConfig.tableModeConfig.tableHeaderOffset}px;"
			>
				<IconButton
					iconName="gear"
					size="small"
					onClick={() => {
						//Toggle the window
						showTableSetting = !showTableSetting;
					}}
				/>

				{#if showTableSetting}
					<div
						class="absolute right-8 top-2 p-4 border-2 border-gray bg-white dark:border-dark-400 dark:bg-dark-400 overflow-y-auto"
						style="max-height: 500px;"
					>
						{#each store.listFilter.filterOptions as filter}
							{#if filter.tableOption?.allowToggleVisibility}
								<div>
									<p>{filter.label}</p>
									<DefaultSwitch
										dataTestId={'option-' + filter.key}
										propertyName="-"
										checked={filter.tableOption?.showAsTableHeader != null
											? filter.tableOption.showAsTableHeader
											: true}
										onCheckChanged={(checked) => {
											if (filter.tableOption) {
												filter.tableOption.showAsTableHeader = checked;
											} else {
												filter.tableOption = {
													showAsTableHeader: checked
												};
											}
											onTableHeaderVisibilityChange();
										}}
									/>
								</div>
							{/if}
						{/each}
					</div>
				{/if}
			</div>
		{/if}
	</div>
</div>
